import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, BarChart3, Brain, Layers, ArrowRight, CheckCircle, Server } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import './LandingPage.css';

export default function LandingPage() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  useDocumentTitle('Technology Procurement Intelligence');

  // Redirect authenticated users to tech-stack
  useEffect(() => {
    if (!loading && user) {
      navigate('/home', { replace: true });
    }
  }, [user, loading, navigate]);

  if (loading) return null;

  return (
    <div className="landing-page">
      {/* Navigation */}
      <header className="landing-nav">
        <div className="landing-nav-inner">
          <div className="landing-brand">
            <div className="landing-logo-mark">
              <Server size={18} />
            </div>
            <span className="landing-logo-text">TekStak</span>
          </div>
          <div className="landing-nav-actions">
            <button
              className="landing-btn-ghost"
              onClick={() => navigate('/sandbox')}
            >
              Try Sandbox
            </button>
            <button
              className="landing-btn-ghost"
              onClick={() => navigate('/login')}
            >
              Sign In
            </button>
            <button
              className="landing-btn-primary"
              onClick={() => navigate('/signup')}
            >
              Request Access
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="landing-hero">
        <div className="landing-hero-inner">
          <div className="landing-hero-badge">
            Early Access Program
          </div>
          <h1 className="landing-hero-title">
            Procurement Intelligence
            <span className="landing-hero-accent"> for Government IT</span>
          </h1>
          <p className="landing-hero-subtitle">
            TekStak helps state and local IT teams classify, analyze, and manage
            technology procurement spend with AI-powered automation and
            Zero Trust security assessments.
          </p>
          <div className="landing-hero-actions">
            <button
              className="landing-btn-primary landing-btn-lg"
              onClick={() => navigate('/signup')}
            >
              Request Early Access
              <ArrowRight size={18} />
            </button>
            <button
              className="landing-btn-outline landing-btn-lg"
              onClick={() => navigate('/sandbox')}
            >
              Try Free Sandbox
            </button>
          </div>
          <div className="landing-hero-stats">
            <div className="landing-stat">
              <span className="landing-stat-value">57K+</span>
              <span className="landing-stat-label">Line Items Classified</span>
            </div>
            <div className="landing-stat-divider" />
            <div className="landing-stat">
              <span className="landing-stat-value">$1.66B+</span>
              <span className="landing-stat-label">Procurement Analyzed</span>
            </div>
            <div className="landing-stat-divider" />
            <div className="landing-stat">
              <span className="landing-stat-value">485+</span>
              <span className="landing-stat-label">Tech Components</span>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="landing-features">
        <div className="landing-features-inner">
          <h2 className="landing-section-title">
            What you get with TekStak
          </h2>
          <p className="landing-section-subtitle">
            Purpose-built tools for government procurement teams to gain visibility
            into technology spend and vendor relationships.
          </p>
          <div className="landing-features-grid">
            <div className="landing-feature-card">
              <div className="landing-feature-icon icon-blue">
                <Brain size={24} />
              </div>
              <h3>AI-Powered Classification</h3>
              <p>
                Multi-tier classification engine using rules, ML, and GenAI
                to automatically categorize procurement items with 95%+ accuracy.
              </p>
            </div>
            <div className="landing-feature-card">
              <div className="landing-feature-icon icon-green">
                <BarChart3 size={24} />
              </div>
              <h3>Spend Flow Visualization</h3>
              <p>
                Interactive Sankey diagrams show how procurement dollars flow from
                departments through vendors to technology categories.
              </p>
            </div>
            <div className="landing-feature-card">
              <div className="landing-feature-icon icon-purple">
                <Shield size={24} />
              </div>
              <h3>Zero Trust Assessments</h3>
              <p>
                Evaluate your technology stack against Zero Trust framework
                pillars with vendor overlays and gap analysis.
              </p>
            </div>
            <div className="landing-feature-card">
              <div className="landing-feature-icon icon-amber">
                <Layers size={24} />
              </div>
              <h3>Tech Stack Management</h3>
              <p>
                Maintain a comprehensive inventory of technology components,
                vendor relationships, and capability mappings.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="landing-trust">
        <div className="landing-trust-inner">
          <h2 className="landing-section-title">Built for government security standards</h2>
          <div className="landing-trust-items">
            <div className="landing-trust-item">
              <CheckCircle size={20} />
              <span>SCPRS Integration</span>
            </div>
            <div className="landing-trust-item">
              <CheckCircle size={20} />
              <span>Role-Based Access Control</span>
            </div>
            <div className="landing-trust-item">
              <CheckCircle size={20} />
              <span>Audit Logging</span>
            </div>
            <div className="landing-trust-item">
              <CheckCircle size={20} />
              <span>Zero Trust Framework</span>
            </div>
            <div className="landing-trust-item">
              <CheckCircle size={20} />
              <span>JWT Authentication</span>
            </div>
            <div className="landing-trust-item">
              <CheckCircle size={20} />
              <span>SSL/TLS Encrypted</span>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="landing-cta">
        <div className="landing-cta-inner">
          <h2>Interested in early access?</h2>
          <p>
            We're onboarding select government IT teams during our early access program.
            Request an invitation to get started.
          </p>
          <button
            className="landing-btn-primary landing-btn-lg"
            onClick={() => navigate('/signup')}
          >
            Request Access
            <ArrowRight size={18} />
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="landing-footer-inner">
          <div className="landing-footer-brand">
            <div className="landing-logo-mark landing-logo-mark-sm">
              <Server size={14} />
            </div>
            <span className="landing-logo-text">TekStak</span>
          </div>
          <div className="landing-footer-links">
            <a href="/terms">Terms of Service</a>
            <a href="/privacy">Privacy Policy</a>
            <a href="mailto:support@tekstak.app">Contact</a>
          </div>
          <p className="landing-footer-copy">
            &copy; {new Date().getFullYear()} TekStak. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
